import { Tooltip, Typography } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LoadingOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import trans from "../../../../utils";
import { classifyDelta } from "../retail/retailCompare";

const { Text } = Typography;

// Tinted pill per outcome. Text stays in dark ink; the outcome is also spelled
// out ("Better"/"Worse") and shown by the arrow, never by colour alone.
const TONES = {
  better: { background: "#e8f6ee", border: "#b7e3c8", icon: "#15803d" },
  worse: { background: "#fdeeee", border: "#f5c2c2", icon: "#b91c1c" },
  neutral: { background: "#f1f5f9", border: "#dbe3ec", icon: "#64748b" },
  na: { background: "#f8fafc", border: "#e2e8f0", icon: "#94a3b8" },
};
const INK = "#1e293b";
const MINUS = "−";

// Round to the displayed precision first so the sign, arrow and colour always
// agree with the number shown; `|| 0` drops -0.
const roundForDisplay = (value, digits) =>
  Number.isFinite(value) ? Number(value.toFixed(digits)) || 0 : value;

const formatMagnitude = (value, digits) =>
  Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: digits });

const formatSigned = (value, digits) =>
  `${value > 0 ? "+" : value < 0 ? MINUS : ""}${formatMagnitude(value, digits)}`;

/**
 * Change of a metric versus the comparison month.
 * - unit "points": `delta` is the difference between two percentages
 *   (12% -> 15% is 3 points).
 * - unit "count": `delta` is `{ abs, pct, isNew }` from countDelta().
 * `from`/`to` are the formatted values of the comparison and selected month;
 * `fromLabel`/`toLabel` name the months. `variant="outcome"` adds the outcome
 * word ("Better"/"Worse") to the pill; `variant="detailed"` also prints
 * "Sep 12.0% -> Oct 15.0%" above it.
 */
export default function DeltaBadge({
  delta,
  unit = "points",
  positiveIsGood = true,
  neutralBand = 0,
  status,
  lowSample = false,
  from,
  to,
  fromLabel,
  toLabel,
  note,
  variant = "pill",
  fontSize = 12,
}) {
  if (status === "loading") {
    return (
      <Text type="secondary" style={{ fontSize }} aria-live="polite">
        <LoadingOutlined aria-hidden="true" /> {trans("Comparing")}
      </Text>
    );
  }

  const isCount = unit === "count";
  const value = roundForDisplay(isCount ? delta?.abs : delta, isCount ? 0 : 1);
  const isNew = isCount ? Boolean(delta?.isNew) : status === "new";
  const tone = isNew ? "na" : classifyDelta(value, { positiveIsGood, neutralBand });
  const colors = TONES[lowSample && tone !== "na" ? "neutral" : tone];

  let amount;
  if (isNew) {
    amount = trans("New");
  } else if (tone === "na") {
    amount = trans("No data");
  } else if (value === 0) {
    amount = trans("No change");
  } else if (isCount) {
    const pct = roundForDisplay(delta.pct, 1);
    amount =
      pct === null || pct === undefined
        ? formatMagnitude(value, 0)
        : `${formatMagnitude(value, 0)} (${formatSigned(pct, 1)}%)`;
  } else {
    amount = `${formatMagnitude(value, 1)} ${trans("points")}`;
  }

  const Icon =
    tone === "na" || isNew || value === 0
      ? tone === "na" || isNew
        ? null
        : MinusOutlined
      : value > 0
        ? ArrowUpOutlined
        : ArrowDownOutlined;
  const outcome =
    tone === "better"
      ? trans("Better")
      : tone === "worse"
        ? trans("Worse")
        : null;
  const valuesText =
    from !== undefined && to !== undefined
      ? `${fromLabel ? `${fromLabel}: ` : ""}${from} → ${toLabel ? `${toLabel}: ` : ""}${to}`
      : null;
  const description = [
    valuesText,
    `${Icon === ArrowUpOutlined ? trans("Up") : Icon === ArrowDownOutlined ? trans("Down") : ""} ${amount}`.trim(),
    outcome,
    lowSample ? trans("Low sample: fewer than 30 stock checks") : null,
    note,
  ]
    .filter(Boolean)
    .join(" · ");

  const pill = (
    <span
      role="img"
      tabIndex={0}
      aria-label={description}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 8px",
        borderRadius: 999,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        color: INK,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      {Icon ? (
        <Icon aria-hidden="true" style={{ color: colors.icon, fontSize: fontSize - 1 }} />
      ) : null}
      <bdi>{amount}</bdi>
      {variant !== "pill" && outcome && !lowSample ? (
        <span style={{ fontWeight: 500, color: "#475569" }}>· {outcome}</span>
      ) : null}
    </span>
  );

  const tooltip = (
    <Tooltip title={description} trigger={["hover", "focus"]}>
      {pill}
    </Tooltip>
  );

  if (variant !== "detailed" || !valuesText) return tooltip;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 3 }}>
      <Text style={{ fontSize: fontSize - 1, color: "#475569" }}>
        <bdi>{valuesText}</bdi>
      </Text>
      {tooltip}
    </span>
  );
}
